// src/pages/HomePage.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import {
  getSubredditPosts,
  searchSubredditPosts,
  getSubredditAbout,
} from "../api/redditApi";
import axios from "axios";
import { SubredditAboutData, PostData } from "../types/reddit";
import PostCard from "../components/PostCard";
import NsfwWarningModal from "../components/NsfwWarningModal";
import {
  BLOCK_NSFW,
  getSessionNsfwConsent,
  setSessionNsfwConsent,
} from "../utils/settings";

// Type for Top Sort Timeframe
type TopTimeframe = "hour" | "day" | "week" | "month" | "year" | "all";

// Available sort options
const LISTING_SORTS: ("hot" | "new" | "top" | "rising")[] = [
  "hot",
  "new",
  "top",
  "rising",
];
const SEARCH_SORTS: ("relevance" | "hot" | "top" | "new" | "comments")[] = [
  "relevance",
  "hot",
  "top",
  "new",
  "comments",
];
const TOP_TIMEFRAME_OPTIONS: TopTimeframe[] = [
  "hour",
  "day",
  "week",
  "month",
  "year",
  "all",
];

// Helper: Format date like PaperMod: January 2, 2006
const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper: Check if a sort string is a valid listing sort
const isValidListingSort = (
  sort: string | null
): sort is "hot" | "new" | "top" | "rising" => {
  return !!sort && LISTING_SORTS.includes(sort as any);
};

// Helper: Check if a sort string is a valid search sort
const isValidSearchSort = (
  sort: string | null
): sort is "relevance" | "hot" | "top" | "new" | "comments" => {
  return !!sort && SEARCH_SORTS.includes(sort as any);
};

// Helper: Check if a timeframe string is valid
const isValidTimeframe = (t: string | null): t is TopTimeframe => {
  return !!t && TOP_TIMEFRAME_OPTIONS.includes(t as any);
};

const HomePage: React.FC = () => {
  const { subredditName } = useParams<{ subredditName?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive state from URL parameters
  const searchQuery = searchParams.get("q");
  const urlSort = searchParams.get("sort");
  const urlTimeframe = searchParams.get("t");

  // State for posts
  const [posts, setPosts] = useState<PostData[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // State for subreddit info header
  const [subredditInfo, setSubredditInfo] = useState<SubredditAboutData | null>(
    null
  );
  const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  // State for search and sort (initialize based on URL params)
  const [sortOrder, setSortOrder] = useState<
    "hot" | "new" | "top" | "rising" | "relevance" | "comments"
  >(() => {
    if (searchQuery && subredditName) {
      return isValidSearchSort(urlSort) ? urlSort : "relevance";
    } else {
      return isValidListingSort(urlSort) ? urlSort : "hot";
    }
  });
  const [topTimeframe, setTopTimeframe] = useState<TopTimeframe>(() => {
    return isValidTimeframe(urlTimeframe) ? urlTimeframe : "day";
  });
  const [localSearchQuery, setLocalSearchQuery] = useState<string>(
    searchQuery || ""
  );

  // NSFW State
  const [showNsfwWarning, setShowNsfwWarning] = useState<boolean>(false);
  const [isNsfwAllowed, setIsNsfwAllowed] = useState<boolean>(false);

  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    threshold: 0.1,
    rootMargin: "200px",
  });

  // Memoized mapping function
  const mapApiResponseToPostData = useCallback(
    (child: any): PostData => ({
      id: child.data.id,
      name: child.data.name,
      title: child.data.title,
      author: child.data.author,
      subreddit: child.data.subreddit,
      subreddit_name_prefixed: child.data.subreddit_name_prefixed,
      thumbnail: child.data.thumbnail,
      url: child.data.url,
      domain: child.data.domain,
      is_self: child.data.is_self,
      score: child.data.score,
      created_utc: child.data.created_utc,
      num_comments: child.data.num_comments,
      permalink: child.data.permalink,
      post_hint: child.data.post_hint,
      thumbnail_width: child.data.thumbnail_width,
      thumbnail_height: child.data.thumbnail_height,
      selftext: child.data.selftext || "",
      is_video: child.data.is_video,
      over_18: child.data.over_18, // Add over_18
    }),
    []
  );

  // Function to update URL search params based on sort/timeframe state
  const updateUrlParams = useCallback(
    (
      newSort: string,
      newTimeframe?: TopTimeframe,
      newQuery?: string | null
    ) => {
      const params: Record<string, string> = {};
      if (newQuery !== undefined) {
        // Handle query update/removal
        if (newQuery) params.q = newQuery;
      } else if (searchQuery) {
        params.q = searchQuery; // Keep existing query if not explicitly changed
      }

      // Only add sort if it's not the default for the context
      const isSearchContext =
        !!(newQuery !== undefined ? newQuery : searchQuery) && !!subredditName;
      const defaultSort = isSearchContext ? "relevance" : "hot";
      if (newSort !== defaultSort) {
        params.sort = newSort;
      }

      // Only add timeframe if sort is 'top' or 'controversial'
      if ((newSort === "top" || newSort === "controversial") && newTimeframe) {
        // Only add 't' if it's not the default ('day')
        if (newTimeframe !== "day") {
          params.t = newTimeframe;
        }
      }
      setSearchParams(params, { replace: true }); // Use replace to avoid polluting history
    },
    [searchQuery, subredditName, setSearchParams]
  );

  // Fetch Posts Function
  const fetchPosts = useCallback(
    async (
      subreddit: string,
      currentSearchQuery: string | null,
      sort: string,
      timeFrame: TopTimeframe,
      loadAfter: string | null,
      isAllowedNsfw: boolean // Pass NSFW consent status
    ) => {
      // Don't fetch if NSFW is blocked and we know the subreddit is NSFW (via subredditInfo)
      if (BLOCK_NSFW && subredditInfo?.over18) {
        setPostsError("NSFW content is blocked in settings.");
        setIsLoadingPosts(false);
        setIsLoadingMore(false);
        setHasMore(false);
        setPosts([]);
        return;
      }
      // Don't fetch if it's NSFW and consent hasn't been given yet
      if (subredditInfo?.over18 && !isAllowedNsfw) {
        setIsLoadingPosts(false); // Stop loading indicator
        return; // Wait for consent
      }

      if (!loadAfter) {
        setIsLoadingPosts(true);
        setPosts([]);
        setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }
      setPostsError(null);

      try {
        const params: Record<string, any> = { limit: 25 };
        if (loadAfter) params.after = loadAfter;

        let response;
        // Use search API only if query exists AND we are in a specific subreddit context
        if (currentSearchQuery && subreddit) {
          params.sort = sort; // Search API uses 'sort' param directly
          // Add time parameter 't' if sorting by top or controversial in search
          if (sort === "top" || sort === "controversial") params.t = timeFrame;
          response = await searchSubredditPosts(
            subreddit,
            currentSearchQuery,
            params
          );
        }
        // Otherwise, use the listing API (different sort param structure)
        else {
          // Add time parameter 't' only if sorting by top or controversial
          if (sort === "top" || sort === "controversial") params.t = timeFrame;
          // Pass sort as part of the path for listing endpoints
          response = await getSubredditPosts(subreddit, sort as any, params);
        }

        let fetchedPosts =
          response?.data?.children?.map(mapApiResponseToPostData) || [];

        // Filter out NSFW posts if BLOCK_NSFW is true
        if (BLOCK_NSFW) {
          fetchedPosts = fetchedPosts.filter(
            (post: { over_18: any }) => !post.over_18
          );
        }

        const newAfter = response?.data?.after;

        setPosts((prevPosts) =>
          loadAfter ? [...prevPosts, ...fetchedPosts] : fetchedPosts
        );
        setAfter(newAfter);
        // Adjust hasMore based on potentially filtered results
        setHasMore(!!newAfter && fetchedPosts.length > 0);
      } catch (err: any) {
        console.error(`Failed fetch posts:`, err);
        if (err.name !== "CanceledError" && !axios.isCancel(err)) {
          setPostsError(`Failed to load posts. ${err.message}`);
          setHasMore(false);
        }
      } finally {
        setIsLoadingPosts(false);
        setIsLoadingMore(false);
      }
    },
    [mapApiResponseToPostData, subredditInfo]
  ); // Add subredditInfo dependency for NSFW check

  // Fetch Subreddit Info Function & Handle NSFW
  const fetchSubredditInfo = useCallback(async (subreddit: string) => {
    if (!subreddit) return; // Don't fetch if no subreddit name
    setIsLoadingInfo(true);
    setInfoError(null);
    setSubredditInfo(null);
    setShowNsfwWarning(false); // Reset warning
    setIsNsfwAllowed(false); // Reset consent

    try {
      const response = await getSubredditAbout(subreddit);
      if (response && response.kind === "t5") {
        const info = response.data as SubredditAboutData;
        setSubredditInfo(info);

        // Check NSFW status after fetching info
        if (info.over18) {
          if (BLOCK_NSFW) {
            setInfoError(
              `r/${subreddit} is NSFW and content is blocked in settings.`
            );
            // Prevent post fetching by setting error
          } else {
            const consentGiven = getSessionNsfwConsent(
              `subreddit_${subreddit}`
            );
            if (!consentGiven) {
              setShowNsfwWarning(true); // Show modal
              // Post fetching will be paused until consent
            } else {
              setIsNsfwAllowed(true); // Consent already given
              // Proceed to fetch posts (will happen in the other useEffect)
            }
          }
        } else {
          setIsNsfwAllowed(true); // Not NSFW, so allowed by default
          // Proceed to fetch posts
        }
      } else if (response === null) {
        // Handle 404 gracefully
        setInfoError(`Subreddit r/${subreddit} not found.`);
      } else {
        setInfoError(`Could not load details for r/${subreddit}.`);
      }
    } catch (err: any) {
      console.error(`Failed fetch subreddit info:`, err);
      if (err.name !== "CanceledError" && !axios.isCancel(err)) {
        setInfoError(
          `Could not load details for r/${subreddit}. ${err.message}`
        );
      }
    } finally {
      setIsLoadingInfo(false);
    }
  }, []);

  // Handle Subreddit-Specific Search Submission
  const handleSubredditSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subredditName) return; // Should not happen if form is visible, but good check

    const trimmedQuery = localSearchQuery.trim();
    const newSort = trimmedQuery ? "relevance" : "hot";
    setSortOrder(newSort); // Update local state
    setLocalSearchQuery(trimmedQuery); // Update local state
    updateUrlParams(newSort, topTimeframe, trimmedQuery); // Update URL

    // Reset pagination for new search/clear
    setAfter(null);
    setHasMore(true);
    setPosts([]);
    // Fetching will be triggered by the useEffect watching searchParams/sortOrder/timeframe
  };

  // Handle Sort Change
  const handleSortChange = (newSort: typeof sortOrder) => {
    setSortOrder(newSort);
    updateUrlParams(newSort, topTimeframe, searchQuery);
    setAfter(null);
    setHasMore(true);
    setPosts([]); // Reset posts for new sort
  };

  // Handle Timeframe Change
  const handleTimeframeChange = (newTimeframe: TopTimeframe) => {
    setTopTimeframe(newTimeframe);
    updateUrlParams(sortOrder, newTimeframe, searchQuery);
    setAfter(null);
    setHasMore(true);
    setPosts([]); // Reset posts for new timeframe
  };

  // Effect for Initial Fetch / Subreddit Change / URL Param Change / NSFW Consent
  useEffect(() => {
    // Sync state with URL params
    const currentSearchQuery = searchParams.get("q");
    const currentUrlSort = searchParams.get("sort");
    const currentUrlTimeframe = searchParams.get("t");

    setLocalSearchQuery(currentSearchQuery || "");

    const isSearchContext = !!currentSearchQuery && !!subredditName;
    const newSortOrder = isSearchContext
      ? isValidSearchSort(currentUrlSort)
        ? currentUrlSort
        : "relevance"
      : isValidListingSort(currentUrlSort)
      ? currentUrlSort
      : "hot";
    const newTimeframe = isValidTimeframe(currentUrlTimeframe)
      ? currentUrlTimeframe
      : "day";

    setSortOrder(newSortOrder);
    setTopTimeframe(newTimeframe);

    // Reset state for new context (if subreddit changes)
    // Check if the current subredditInfo matches the URL param before resetting
    if (subredditName && subredditName !== subredditInfo?.display_name) {
      setSubredditInfo(null);
      setIsLoadingInfo(false);
      setInfoError(null);
      setShowNsfwWarning(false);
      setIsNsfwAllowed(false);
    } else if (!subredditName && subredditInfo) {
      // Clear subreddit info if navigating back to home feed
      setSubredditInfo(null);
      setIsLoadingInfo(false);
      setInfoError(null);
      setShowNsfwWarning(false);
      setIsNsfwAllowed(true); // Home feed is allowed
    }

    // Reset posts state before fetching
    setAfter(null);
    setHasMore(true);
    setPosts([]);
    setPostsError(null);

    const targetSubreddit = subredditName || "";

    // Fetch subreddit info first if a specific subreddit is being viewed and info isn't loaded/doesn't match
    if (
      subredditName &&
      (!subredditInfo || subredditInfo.display_name !== subredditName)
    ) {
      fetchSubredditInfo(subredditName);
      // Post fetching will be triggered by the fetchSubredditInfo callback or the isNsfwAllowed state change
    } else {
      // Home feed or subreddit info already loaded - fetch posts directly if allowed
      const consent = subredditName ? isNsfwAllowed : true; // Assume allowed for home feed
      if (consent) {
        fetchPosts(
          targetSubreddit,
          currentSearchQuery,
          newSortOrder,
          newTimeframe,
          null,
          consent
        );
      }
    }

    // Trigger effect when subreddit, search query, sort, or timeframe from URL changes
    // Exclude fetchPosts & fetchSubredditInfo from deps as they are memoized and stable
    // Exclude state setters like setSortOrder, setTopTimeframe etc.
    // isNsfwAllowed is needed to trigger refetch after consent
    // subredditInfo is needed to check if info matches URL before fetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subredditName,
    searchParams,
    fetchSubredditInfo,
    fetchPosts,
    isNsfwAllowed,
    subredditInfo,
  ]); // Depend on searchParams directly

  // Effect for Loading More Posts (Infinite Scroll)
  useEffect(() => {
    const targetSubreddit = subredditName || "";
    if (
      loadMoreInView &&
      !isLoadingPosts &&
      !isLoadingMore &&
      hasMore &&
      after &&
      isNsfwAllowed
    ) {
      // Fetch next page of posts using current context, only if NSFW allowed
      fetchPosts(
        targetSubreddit,
        searchQuery,
        sortOrder,
        topTimeframe,
        after,
        isNsfwAllowed
      );
    }
    // Trigger when observer comes into view or relevant state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadMoreInView,
    isLoadingPosts,
    isLoadingMore,
    hasMore,
    after,
    subredditName,
    sortOrder,
    searchQuery,
    topTimeframe,
    isNsfwAllowed,
  ]);

  // NSFW Modal Handlers
  const handleNsfwConsent = () => {
    if (!subredditName) return;
    setSessionNsfwConsent(`subreddit_${subredditName}`); // Store consent for this session
    setIsNsfwAllowed(true); // This will trigger the useEffect to fetch posts
    setShowNsfwWarning(false);
  };

  const handleNsfwDecline = () => {
    // Navigate back or show an error/different view
    setInfoError(`Viewing r/${subredditName} declined due to NSFW warning.`);
    setShowNsfwWarning(false);
    setPosts([]); // Clear any potentially loaded posts
    setHasMore(false);
    // Optionally navigate away: navigate('/');
  };

  const availableSorts =
    searchQuery && subredditName ? SEARCH_SORTS : LISTING_SORTS;
  const currentSubredditDisplay = subredditName
    ? `r/${subredditName}`
    : "Home Feed";
  const isInitialLoading =
    isLoadingPosts ||
    (!!subredditName && !searchQuery && isLoadingInfo && !subredditInfo); // Loading if posts OR (subreddit page AND info is loading AND info is not yet set)
  const shouldShowContent = isNsfwAllowed || (!subredditName && !searchQuery); // Show content if allowed or on home/search
  const redditSubredditUrl = subredditInfo
    ? `https://www.reddit.com${subredditInfo.url}`
    : "#";

  // --- Render Logic ---
  return (
    <div className="content-wrapper space-y-10">
      {/* NSFW Warning Modal */}
      {showNsfwWarning && subredditName && (
        <NsfwWarningModal
          contentType="subreddit"
          onConfirm={handleNsfwConsent}
          onDecline={handleNsfwDecline}
        />
      )}

      {/* Page Header */}
      <header className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {searchQuery
            ? `Search results in ${currentSubredditDisplay}`
            : currentSubredditDisplay}
          {subredditInfo?.over18 && (
            <span className="ml-2 text-xs font-semibold uppercase tracking-wider bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">
              NSFW
            </span>
          )}
        </h1>
        {/* Subreddit Info Header (Only show on subreddit page, not home or search results) */}
        {subredditName && !searchQuery && (
          <div className="pb-2 space-y-2">
            {isLoadingInfo &&
              !subredditInfo && ( // Show loading only if no info yet
                <div className="space-y-2 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-lg"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                </div>
              )}
            {infoError &&
              !isLoadingInfo && ( // Show errors
                <p className="text-sm text-red-600 dark:text-red-400">
                  {infoError}
                </p>
              )}
            {subredditInfo &&
              !isLoadingInfo &&
              !infoError && ( // Show info if loaded and no error
                <>
                  {subredditInfo.public_description && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {subredditInfo.public_description}
                    </p>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {subredditInfo.subscribers > 0 && (
                      <span>
                        {subredditInfo.subscribers.toLocaleString()} members
                      </span>
                    )}
                    {subredditInfo.subscribers > 0 &&
                      subredditInfo.created_utc > 0 && (
                        <span aria-hidden="true">•</span>
                      )}
                    {subredditInfo.created_utc > 0 && (
                      <span>
                        Created {formatDate(subredditInfo.created_utc)}
                      </span>
                    )}
                    {/* --- ADDED: View on Reddit Link --- */}
                    {subredditInfo.url && (
                      <>
                        <span aria-hidden="true">•</span>
                        <a
                          href={redditSubredditUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center hover:underline"
                          title={`View ${subredditInfo.display_name_prefixed} on Reddit`}
                        >
                          reddit.com
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 ml-0.5 opacity-70"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                          </svg>
                        </a>
                      </>
                    )}
                    {/* --- End View on Reddit Link --- */}
                  </div>
                </>
              )}
          </div>
        )}

        {/* Search and Sort Controls Container */}
        {/* Show controls if on subreddit page OR home feed (not global search results page) AND NSFW allowed (or not applicable) */}
        {(subredditName || !searchQuery) && shouldShowContent && !infoError && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            {/* Sort Controls (Left) */}
            <div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
              {(availableSorts as string[]).map((sort) => (
                <button
                  key={sort}
                  onClick={() => handleSortChange(sort as any)} // Use handler
                  className={`text-sm px-1 capitalize ${
                    sortOrder === sort
                      ? "font-semibold text-indigo-700 dark:text-indigo-400"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {sort}
                </button>
              ))}
              {/* Timeframe Selector - Conditionally Rendered for 'top' sort */}
              {sortOrder === "top" && (
                <select
                  value={topTimeframe}
                  onChange={(e) =>
                    handleTimeframeChange(e.target.value as TopTimeframe)
                  } // Use handler
                  className="ml-2 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
                  aria-label="Select time frame for top posts"
                >
                  {TOP_TIMEFRAME_OPTIONS.map((t) => (
                    <option key={t} value={t} className="capitalize">
                      {t}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Subreddit Search Form (Right - only on subreddit pages) */}
            {subredditName && (
              <form
                onSubmit={handleSubredditSearch}
                className="w-full sm:w-auto sm:max-w-xs"
              >
                <div className="relative">
                  <input
                    type="search"
                    value={localSearchQuery}
                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                    placeholder={`Search r/${subredditName}...`}
                    className="w-full pl-8 pr-8 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                    aria-label={`Search within r/${subredditName}`}
                  />
                  {/* Search Icon */}
                  <div className="absolute left-0 top-0 h-full flex items-center pl-2 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400 dark:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  {/* Clear Button */}
                  {localSearchQuery && (
                    <button
                      type="button"
                      aria-label="Clear Search"
                      onClick={() => {
                        setLocalSearchQuery("");
                        const newSort = "hot"; // Reset sort to hot when clearing search
                        setSortOrder(newSort);
                        updateUrlParams(newSort, topTimeframe, ""); // Clear query param, reset sort in URL
                        setAfter(null);
                        setHasMore(true);
                        setPosts([]); // Reset posts
                      }}
                      className="absolute right-0 top-0 h-full px-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        )}
      </header>

      {/* Loading Skeleton - Show only if content should be shown but is loading */}
      {isInitialLoading && shouldShowContent && !infoError && (
        <div className="space-y-6 animate-pulse pt-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="p-4 md:p-5 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50 flex gap-4"
            >
              <div className="flex-1 space-y-3">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-200 dark:bg-gray-700 rounded-md flex-shrink-0"></div>
            </div>
          ))}
        </div>
      )}

      {/* Posts Error Message - Show if not loading and content should be shown */}
      {postsError && !isLoadingPosts && !isLoadingMore && shouldShowContent && (
        <div
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm"
          role="alert"
        >
          <strong>Error loading posts:</strong> {postsError}
        </div>
      )}

      {/* Posts List - Using PostCard - Show if not initial loading, content allowed, no errors */}
      {!isInitialLoading &&
        shouldShowContent &&
        !infoError &&
        !postsError &&
        posts.length > 0 && (
          <section className="space-y-4">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                // Show subreddit link if on home feed (no subredditName) or global search results
                isHomePage={!subredditName}
                // Pass consent status for thumbnail handling
                isNsfwAllowedForSubreddit={isNsfwAllowed}
              />
            ))}
          </section>
        )}

      {/* Load More Trigger & Indicator - Show if relevant conditions met */}
      {!isInitialLoading &&
        hasMore &&
        posts.length > 0 &&
        shouldShowContent &&
        !infoError &&
        !postsError && (
          <div
            ref={loadMoreRef}
            className="h-20 flex justify-center items-center pt-6"
          >
            {isLoadingMore && (
              <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 text-sm">
                <svg
                  className="animate-spin h-4 w-4 text-indigo-600 dark:text-indigo-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Loading...</span>
              </div>
            )}
          </div>
        )}
      {/* End of Results Indicator */}
      {!isInitialLoading &&
        !hasMore &&
        posts.length > 0 &&
        shouldShowContent &&
        !infoError &&
        !postsError && (
          <div className="h-10 flex justify-center items-center pt-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              End of results.
            </p>
          </div>
        )}

      {/* No Posts Found State */}
      {!isInitialLoading &&
        !postsError &&
        posts.length === 0 &&
        (subredditName || searchQuery) &&
        shouldShowContent &&
        !infoError && (
          <div className="text-center text-gray-500 dark:text-gray-400 pt-10">
            <p>
              No posts found {searchQuery ? `matching "${searchQuery}"` : ""} in{" "}
              {currentSubredditDisplay}.
            </p>
          </div>
        )}
      {/* Initial state message for Home Feed */}
      {!subredditName &&
        !isInitialLoading &&
        posts.length === 0 &&
        !postsError &&
        !searchQuery && (
          <div className="text-center text-gray-500 dark:text-gray-400 pt-10">
            <p>
              Welcome to the feed. Use search or navigate via URL (e.g.,
              /r/news).
            </p>
          </div>
        )}
      {/* Message if content isn't shown due to NSFW decline/block */}
      {!shouldShowContent && !isLoadingInfo && infoError && (
        <div className="text-center text-gray-500 dark:text-gray-400 pt-10">
          <p>{infoError}</p>
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 hover:underline mt-2 inline-block"
          >
            Go Home
          </Link>
        </div>
      )}
    </div>
  );
};

export default HomePage;
